import { JobFunction, UserRole } from '@atlas/types'
import { z } from 'zod'

// ─── Public-facing schema (create / update inputs) ────────────────────────────

export const userCreateSchema = z.object({
	email: z.email(),
	displayName: z.string().min(1),
	role: z.enum(UserRole).optional(),
	jobFunction: z.enum(JobFunction).optional(),
	isActive: z.boolean().default(true),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	avatarUrl: z.url().optional(),
})

export const userUpdateSchema = userCreateSchema.partial()

export type UserCreateInput = z.infer<typeof userCreateSchema>
export type UserUpdateInput = z.infer<typeof userUpdateSchema>

// ─── Admin provisioning input (ADR-0013) ──────────────────────────────────────
// `POST /admin/users` accepts EXACTLY `{ email, displayName, role }`. `.strict()`
// rejects unknown or system-managed fields (isActive, cognitoSub, userId, …) so
// they can never be set from the browser; the controller returns 400 on failure.
export const adminUserProvisionSchema = z
	.object({
		email: z.email(),
		displayName: z.string().min(1),
		role: z.enum(UserRole),
	})
	.strict()

export type AdminUserProvisionInput = z.infer<typeof adminUserProvisionSchema>

// `PATCH /admin/users/:userId/role` accepts EXACTLY `{ role }` (ADR-0013). `.strict()`
// rejects unknown fields; `z.enum(UserRole)` rejects invalid roles → 400 before any write.
export const adminRoleChangeSchema = z.object({ role: z.enum(UserRole) }).strict()
export type AdminRoleChangeInput = z.infer<typeof adminRoleChangeSchema>

// `PATCH /admin/users/:userId/job-function` accepts EXACTLY `{ jobFunction }`,
// following the same one-field-per-endpoint shape as the role and status changes
// (ADR-0013): a job function is a distinct privileged edit, not a profile field.
export const adminJobFunctionChangeSchema = z.object({ jobFunction: z.enum(JobFunction) }).strict()
export type AdminJobFunctionChangeInput = z.infer<typeof adminJobFunctionChangeSchema>

// `PATCH /admin/users/:userId/status` accepts EXACTLY `{ isActive }` (ADR-0013).
export const adminStatusChangeSchema = z.object({ isActive: z.boolean() }).strict()
export type AdminStatusChangeInput = z.infer<typeof adminStatusChangeSchema>

// Profile-only update for the legacy `PUT /users/{id}`: `role`, `job_function` and
// `is_active` are NOT mutable here (use the dedicated admin PATCH endpoints).
// Privileged fields are omitted from the shape, so they are stripped rather than applied.
export const userProfileUpdateSchema = userCreateSchema
	.omit({ role: true, isActive: true, jobFunction: true })
	.partial()
export type UserProfileUpdateInput = z.infer<typeof userProfileUpdateSchema>
